import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../interceptors";

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min — matches plan-doc

const keyFor = ({ userId, interviewId, dimension }) =>
  `${userId ?? ""}-${interviewId ?? ""}-${dimension ?? "overall"}`;

export const fetchPercentile = createAsyncThunk(
  "percentile/fetch",
  async ({ userId, interviewId, dimension = "overall" }, { getState, rejectWithValue }) => {
    if (!userId) return rejectWithValue("userId required");
    const state = getState().percentileSliceReducer;
    const k = keyFor({ userId, interviewId, dimension });
    const cached = state.byKey[k];
    if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      return { fromCache: true, key: k, data: cached.data };
    }
    try {
      const params = new URLSearchParams();
      params.set("dimension", dimension);
      if (interviewId != null) params.set("interviewId", String(interviewId));
      const r = await axiosInstance.get(
        `/job-posting/api/candidate-report/${userId}/percentile?${params.toString()}`,
      );
      return { fromCache: false, key: k, data: r.data || {} };
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to load percentile");
    }
  },
);

const percentileSlice = createSlice({
  name: "percentile",
  initialState: {
    byKey: {}, // key -> { data, fetchedAt, status }
  },
  reducers: {
    invalidatePercentile(state) {
      state.byKey = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPercentile.pending, (state, { meta }) => {
        const k = keyFor(meta.arg);
        state.byKey[k] = { ...(state.byKey[k] || {}), status: "loading" };
      })
      .addCase(fetchPercentile.fulfilled, (state, { payload }) => {
        if (payload.fromCache) return;
        state.byKey[payload.key] = {
          data: payload.data,
          fetchedAt: Date.now(),
          status: "succeeded",
        };
      })
      .addCase(fetchPercentile.rejected, (state, { meta, payload }) => {
        const k = keyFor(meta.arg);
        state.byKey[k] = { ...(state.byKey[k] || {}), status: "failed", error: payload };
      });
  },
});

export const { invalidatePercentile } = percentileSlice.actions;

export const selectPercentile = (args) => (s) => {
  const k = keyFor(args);
  return s.percentileSliceReducer.byKey[k]?.data || null;
};

export const selectPercentileStatus = (args) => (s) => {
  const k = keyFor(args);
  return s.percentileSliceReducer.byKey[k]?.status || "idle";
};

export default percentileSlice.reducer;
